/* =====================================================================================
   Create operations for Channel, Studio, Contracts and Ledger.

   Everything here writes real rows. The buttons these back were previously decorative,
   which is worse than a missing button: a control that does nothing teaches people the
   system is broken and they stop trying.

   Two rules hold across all four:

     * every record needs a counterparty that already exists as an account, so the CRM stays
       the single place a company is described rather than each module inventing its own
     * status starts at the beginning of its lifecycle, never at the end. A partner starts
       prospective, a contract starts draft, an invoice starts draft.
   ===================================================================================== */

import { BACKEND } from './db';
import { dbSiteId } from './ids';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

async function orgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}

async function meId(): Promise<string | null> {
  const { currentMember } = await import('./supabase');
  return (await currentMember())?.id ?? null;
}

function fail(error: { code?: string; message: string }, what: string): never {
  if (error.code === '23505') throw new Error(`That ${what} reference already exists.`);
  if (error.code === '42501') throw new Error('Refused by row level security.');
  if (error.code === '23503') {
    throw new Error(`${what}: the account or related record does not exist yet. Create it first.`);
  }
  throw new Error(explain(error.message, 'save this record'));
}

/* -------------------------------------------------------------------------------------
   Accounts — the shared counterparty list
   ------------------------------------------------------------------------------------- */
export interface Account { id: string; name: string; kinds: string[] | null; country: string | null }

export async function fetchAccounts(): Promise<Account[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('crm').from('accounts').select('id, name, kinds, country').order('name');
  if (error) throw new Error(explain(error.message, 'save this record'));
  return data ?? [];
}

/** Create a counterparty. Needed before a partner, contract or invoice can point at one. */
/* `kinds` is an array because one organisation is often several things at once: a hospital
   that is also an H&I laboratory and also the invoiced entity. Forcing a single type is what
   makes CRMs need a "primary type" field and then argue with themselves about it. */
export const ACCOUNT_KINDS = ['transplant_centre', 'hi_lab', 'distributor', 'payer',
  'procurement_body', 'supplier', 'partner', 'funder', 'other'] as const;

export async function createAccount(a: { name: string; kinds: string[]; country?: string }) {
  const { supabase } = await import('./supabase');
  /* No status here: the column default is 'prospect', which is what a counterparty created
     on the fly is. This used to send 'active', a word from an older vocabulary the CHECK no
     longer admits, so every inline create was refused. Provenance stamped as operator, the
     same contract Customers follows, so Atlas never overwrites what a person typed. */
  const { data, error } = await supabase().schema('crm').from('accounts').insert({
    org_id: await orgId(), name: a.name, kinds: a.kinds.length ? a.kinds : ['other'],
    country: a.country || null,
    field_sources: { name: 'operator', ...(a.country ? { country: 'operator' } : {}) },
  }).select('id').single();
  if (error) fail(error, 'account');
  return data.id as string;
}

/* -------------------------------------------------------------------------------------
   Channel
   ------------------------------------------------------------------------------------- */
export const PARTNER_TYPES = ['distributor', 'agent', 'authorised_representative',
  'importer', 'service_lab', 'reseller', 'referral'] as const;

export async function createPartner(p: {
  accountId: string; partnerType: string; countries?: string[];
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('channel').from('partners').insert({
    org_id: await orgId(), account_id: p.accountId, partner_type: p.partnerType,
    // Prospective, not active. A partner becomes active when due diligence passes, and
    // migration 0004 blocks orders against one whose diligence has lapsed.
    status: 'prospective',
  });
  if (error) fail(error, 'partner');
}

export const EXCLUSIVITY = ['exclusive', 'semi_exclusive', 'non_exclusive'] as const;

/**
 * Grant a territory. The database carries a GiST exclusion constraint that makes two
 * overlapping exclusive grants for the same country and product impossible, so a
 * double-appointment is refused rather than discovered two years later in a dispute.
 */
export async function createTerritoryGrant(t: {
  partnerId: string; country: string; exclusivity: string;
  from: string; to?: string; regionScope?: string;
  minCommitmentValue?: number; commitmentCurrency?: string; noticeDays?: number;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('channel').from('territory_grants').insert({
    org_id: await orgId(), partner_id: t.partnerId,
    country: t.country.toUpperCase(),
    region_scope: t.regionScope || null,
    exclusivity: t.exclusivity,
    valid_period: `[${t.from},${t.to || ''})`,
    min_commitment_value: t.minCommitmentValue ?? null,
    commitment_currency: t.commitmentCurrency || 'GBP',
    notice_days: t.noticeDays ?? 90,
    status: 'active',
  });
  if (error) {
    if (error.code === '23P01') {
      throw new Error(
        'Refused: that overlaps an existing exclusive grant for the same country and product. ' +
        'Two exclusive appointments in one territory is how distributor disputes start.',
      );
    }
    fail(error, 'territory grant');
  }
}

export const TENDER_STATUS = ['identified', 'qualifying', 'preparing', 'submitted',
  'clarification', 'awarded_us', 'awarded_other', 'cancelled', 'abandoned'] as const;

/** A tender we may bid for directly, without a partner. */
export async function createTender(t: {
  title: string; authorityName: string; country: string;
  submissionDeadline?: string; estimatedValue?: number; currency?: string;
  partnerId?: string | null; portalUrl?: string; reference?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('channel').from('tenders').insert({
    org_id: await orgId(), title: t.title, authority_name: t.authorityName,
    country: t.country.toUpperCase(),
    reference: t.reference || null,
    submission_deadline: t.submissionDeadline || null,
    estimated_value: t.estimatedValue ?? null,
    currency: t.currency || 'GBP',
    // Null partner means we bid directly. That is a real option and the schema allows it.
    partner_id: t.partnerId || null,
    portal_url: t.portalUrl || null,
    status: 'identified',
  });
  if (error) fail(error, 'tender');
}

/* -------------------------------------------------------------------------------------
   Studio
   ------------------------------------------------------------------------------------- */
export const ASSET_TYPES = ['brochure', 'detail_aid', 'poster', 'abstract', 'slide_deck',
  'website_page', 'email', 'social_post', 'video', 'press_release', 'ifu', 'other'] as const;

export async function createAsset(a: {
  jobCode: string; title: string; assetType: string;
  territories?: string[]; description?: string; audience?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('studio').from('assets').insert({
    org_id: await orgId(), job_code: a.jobCode, title: a.title,
    asset_type: a.assetType,
    territories: a.territories?.length ? a.territories.map((t) => t.toUpperCase()) : [],
    description: a.description || null,
    audience: a.audience || null,
    // The claim level belongs to a VERSION, not the asset: version 2 may claim more than
    // version 1 did, and the gate has to judge the version that is actually in review.
    owner_id: await meId(),
    status: 'draft',
  });
  if (error) fail(error, 'asset');
}

/* -------------------------------------------------------------------------------------
   Contracts
   ------------------------------------------------------------------------------------- */
export const CONTRACT_TYPES = ['distribution', 'supply', 'services', 'nda', 'collaboration',
  'licence', 'employment', 'consultancy', 'lease', 'software', 'other'] as const;

/* Execution states, in lifecycle order. `partially_executed` is the one that matters: we
   have signed and they have not, so they hold an option and we hold an obligation. */
export const CONTRACT_STATUS = ['draft', 'in_negotiation', 'final', 'out_for_signature',
  'partially_executed', 'fully_executed', 'active', 'suspended', 'expired',
  'terminated', 'superseded'] as const;

export async function createContract(c: {
  reference: string; title: string; contractType: string; counterpartyName: string;
  counterpartyAccountId?: string | null; value?: number; currency?: string;
  effectiveFrom?: string; initialTermMonths?: number; noticePeriodDays?: number;
  paymentTermsDays?: number; governingLaw?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('contracts').from('contracts').insert({
    org_id: await orgId(), reference: c.reference, title: c.title,
    contract_type: c.contractType,
    counterparty_name: c.counterpartyName,
    counterparty_account_id: c.counterpartyAccountId || null,
    value: c.value ?? null, currency: c.currency || 'GBP',
    effective_from: c.effectiveFrom || null,
    initial_term_months: c.initialTermMonths ?? null,
    // notice_deadline is computed by the database from these two. Never sent from here.
    notice_period_days: c.noticePeriodDays ?? null,
    payment_terms_days: c.paymentTermsDays ?? 30,
    governing_law: c.governingLaw || null,
    owner_id: await meId(),
    status: 'draft',
  });
  if (error) fail(error, 'contract');
}

export async function updateContract(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('contracts').from('contracts').update(patch).eq('id', id);
  if (error) fail(error, 'contract');
}

export interface ContractPosition {
  id: string; reference: string; title: string; counterparty_name: string; status: string;
  contract_value: number | null; currency: string | null;
  effective_from: string | null; expires_on: string | null; notice_deadline: string | null;
  invoiced: number; received: number; pct_invoiced: number | null;
  open_obligations: number; next_obligation: string | null;
}

export async function fetchContractPositions(): Promise<ContractPosition[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('contracts').from('v_contract_position').select('*').order('reference');
  if (error) throw new Error(explain(error.message, 'save this record'));
  return data ?? [];
}

/** Contracts claiming to be executed with no signed copy attached. */
export async function fetchMissingExecutedCopies() {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase()
    .schema('contracts').from('v_missing_executed_copy').select('*');
  return data ?? [];
}

/* -------------------------------------------------------------------------------------
   Ledger
   ------------------------------------------------------------------------------------- */
export async function nextInvoiceNumber(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('ledger').from('invoices')
    .select('number').order('number', { ascending: false }).limit(1);
  const last = data?.[0]?.number as string | undefined;
  const year = new Date().getFullYear();
  const seq = last?.startsWith(`INV-${year}`) ? Number(last.split('-')[2] || 0) + 1 : 1;
  return `INV-${year}-${String(seq).padStart(4, '0')}`;
}

export async function createInvoice(i: {
  number: string; accountId: string; dueDate: string; currency: string;
  subtotal: number; taxTotal?: number; contractId?: string | null;
  issueDate?: string; notes?: string;
}) {
  const { supabase } = await import('./supabase');
  const tax = i.taxTotal ?? 0;
  const { error } = await supabase().schema('ledger').from('invoices').insert({
    org_id: await orgId(), number: i.number, account_id: i.accountId,
    invoice_type: 'invoice',
    contract_id: i.contractId || null,
    issue_date: i.issueDate || new Date().toISOString().slice(0, 10),
    due_date: i.dueDate, currency: i.currency,
    subtotal: i.subtotal, tax_total: tax, total: i.subtotal + tax,
    amount_paid: 0, amount_outstanding: i.subtotal + tax,
    notes: i.notes || null,
    // Draft, not issued. Migration 0006 makes an issued invoice append-only, so issuing is a
    // deliberate second step rather than a side effect of typing a number in.
    status: 'draft',
    created_by: await meId(),
  });
  if (error) fail(error, 'invoice');
}

/**
 * Export for an external accounting package. Deliberately CSV and deliberately not
 * Sage-specific: the target might be Xero, QuickBooks or NetSuite, and every one of them
 * imports a CSV. Building a Sage API integration before the package is chosen would be
 * work thrown away.
 */
export function invoicesToCsv(rows: Array<Record<string, unknown>>): string {
  const cols = ['number', 'issue_date', 'due_date', 'currency', 'subtotal',
                'tax_total', 'total', 'amount_paid', 'status', 'accounting_ref'];
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* -------------------------------------------------------------------------------------
   The commercial chain hanging off one centre.

   centre -> opportunity -> contract -> invoices, in one query. Without this a rep looking at
   Mayo Phoenix cannot see that there is an open opportunity, a signed contract and two
   unpaid invoices against it, which is most of what they need before picking up the phone.
   ------------------------------------------------------------------------------------- */
export interface SiteCommercial {
  site_id: string;
  opportunity_id: string; opportunity_name: string; opportunity_status: string;
  amount: number | null; currency: string | null; expected_close: string | null;
  contract_id: string | null; contract_reference: string | null; contract_status: string | null;
  invoice_count: number; invoiced_total: number; outstanding: number;
}

export async function fetchSiteCommercial(siteId: string): Promise<SiteCommercial[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('atlas').from('v_site_commercial').select('*').eq('site_id', dbSiteId(siteId));
  if (error) throw new Error(explain(error.message, 'save this record'));
  return data ?? [];
}

/** Invoices raised against one contract, so the position table can be clicked through. */
export async function fetchContractInvoices(contractId: string) {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase()
    .schema('ledger').from('invoices')
    .select('id, number, issue_date, due_date, currency, total, amount_paid, amount_outstanding, status')
    .eq('contract_id', contractId).order('issue_date', { ascending: false });
  return data ?? [];
}
